import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, errMsg } from "@/lib/api";
import { EASE } from "@/components/motion";

export const card = "bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:-translate-y-1 transition duration-300";
export const inputCls = "w-full border border-slate-200 rounded-xl p-3 md:p-4 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-800 font-medium bg-white transition";
export const btnPrimary = "inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 md:py-4 px-6 rounded-xl shadow-md hover:bg-blue-700 transform active:scale-95 transition disabled:opacity-60 disabled:pointer-events-none";
export const badge = {
  success: "bg-emerald-500 text-white",
  info: "bg-blue-100 text-blue-700",
  warn: "bg-amber-100 text-amber-700",
  danger: "bg-red-50 text-red-600",
};

export function useApi(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reload = useCallback(
    () => api.get(path).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))),
    [path]
  );
  useEffect(() => {
    reload();
    const onRefresh = () => reload();
    const onVis = () => { if (document.visibilityState === "visible") reload(); };
    window.addEventListener("portal:refresh", onRefresh);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("portal:refresh", onRefresh);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reload]);
  return { data, setData, reload, error };
}

export function PageTitle({ eyebrow, title, right }) {
  return (
    <div className="px-6 pt-12 pb-2 flex items-end justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">{eyebrow}</p>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mt-1">{title}</h1>
      </div>
      {right}
    </div>
  );
}

export function Stagger({ children, className = "" }) {
  return (
    <motion.div className={className} initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } }}>
      {children}
    </motion.div>
  );
}

export const Item = ({ children, className = "", ...rest }) => (
  <motion.div variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } }} className={className} {...rest}>
    {children}
  </motion.div>
);

export const Skeleton = ({ className = "h-32" }) => <div className={`rounded-3xl bg-slate-200/70 animate-pulse ${className}`} />;

export function StatusBadge({ status }) {
  const map = { pending: ["warn", "Menunggu"], verified: ["success", "Terverifikasi"], paid: ["success", "Lunas"], rejected: ["danger", "Ditolak"], failed: ["danger", "Gagal"] };
  const [tone, label] = map[status] || ["info", status];
  return <span data-testid={`status-${status}`} className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[tone]}`}>{label}</span>;
}
